<template>
  <ui-menu-filter-trigger
    data-testid="dropdown-trigger"
    :disabled="disabled"
    :expanded="expanded"
    :can-remove="canRemove"
    :theme="triggerTheme"
    :full-width
    :error
    @remove="emit('remove')"
  >
    <template v-if="$slots.icon" #icon>
      <slot name="icon" />
    </template>

    <template v-if="triggerLabel" #label>
      {{ triggerLabel }}
    </template>

    <span v-if="showTriggerContent" class="truncate" :class="textColor">
      <slot :name="labelSlotName" :count="formattedCount">
        {{ defaultLabelSlotMap[labelSlotName] }}
      </slot>
    </span>
  </ui-menu-filter-trigger>
</template>

<script
  setup
  lang="ts"
  generic="T extends object, P extends keyof T, L extends keyof T, M extends Modes, V extends T, O extends boolean"
>
import { computed, useSlots } from 'vue'

import UiMenuFilterTrigger from '../UiMenu/triggers/UiMenuFilterTrigger.vue'
import { UiMenuTriggerTheme } from '../UiMenu/types'
import { type DropdownProps, SelectAllModes, type Modes } from './types'
import { ALL_ID } from './constants'
import type useList from './composables/useList'

interface Props extends DropdownProps<T, P, L, M, V, O> {
  expanded: boolean
  totalSelectedCount?: number
  selectedOptions: ReturnType<typeof useList<T, P, L, M, V, O>>['selectedOptions']['value']
  formattedCount: string | number
  excludedValues: ReturnType<typeof useList<T, P, L, M, V, O>>['excludedValues']['value']
  selectedIds: ReturnType<typeof useList<T, P, L, M, V, O>>['selectedIds']['value']
  allOptions: ReturnType<typeof useList<T, P, L, M, V, O>>['allOptions']['value']
  searchQuery: string
}

const props = defineProps<Props>()
const emit = defineEmits<{
  remove: []
}>()
const slots = useSlots()

const showTriggerContent = computed(() => props.selectAllMode === SelectAllModes.IntermediaryAll || !props.isLoading)

const defaultLabelSlotMap = computed(() => ({
  'trigger-single-selected': props.label ? props.selectedOptions[0]?.[props.label] : '',
  'trigger-multiple-selected': props.formattedCount,
  'trigger-all-selected': props.selectAllLabel,
  'trigger-placeholder': props.triggerPlaceholder,
}))

const placeholderColors = {
  [UiMenuTriggerTheme.WhiteBordered]: 'text-gray-50',
  [UiMenuTriggerTheme.Light]: 'text-gray-50',
}

const isPlaceholder = computed(() => labelSlotName.value === 'trigger-placeholder')

const textColor = computed(() => {
  if (props.triggerLabel || slots['option-icon']) return 'text-gray-80'

  if (isPlaceholder.value && props.triggerTheme && placeholderColors[props.triggerTheme]) {
    return placeholderColors[props.triggerTheme]
  }

  return 'text-gray-90'
})

const labelSlotName = computed<keyof typeof defaultLabelSlotMap.value>(() => {
  if (props.excludedValues.length) return 'trigger-multiple-selected'
  if (
    props.selectedIds[0] === ALL_ID &&
    (props.allOptions.filter((o) => o[props.propToCheck] !== ALL_ID).length || props.searchQuery)
  ) {
    return 'trigger-all-selected'
  }
  if (props.selectedOptions.length === 1) return 'trigger-single-selected'
  if (props.modelValue.length) return 'trigger-multiple-selected'

  return 'trigger-placeholder'
})
</script>
